import { readKakaoJsKey } from '@/shared/lib/kakaoKey'

let sdkPromise: Promise<KakaoMaps> | null = null

export interface KakaoMaps {
  LatLng: new (lat: number, lng: number) => KakaoLatLng
  LatLngBounds: new () => { extend: (p: KakaoLatLng) => void }
  Map: new (
    el: HTMLElement,
    opts: { center: KakaoLatLng; level: number; draggable?: boolean; scrollwheel?: boolean },
  ) => KakaoMap
  Marker: new (opts: { position: KakaoLatLng }) => { setMap: (m: KakaoMap | null) => void }
  CustomOverlay: new (opts: {
    position: KakaoLatLng
    content: HTMLElement
    yAnchor?: number
    xAnchor?: number
    clickable?: boolean
  }) => { setMap: (m: KakaoMap | null) => void }
  Polygon: new (opts: {
    path: KakaoLatLng[]
    strokeWeight?: number
    strokeColor?: string
    strokeOpacity?: number
    fillColor?: string
    fillOpacity?: number
  }) => KakaoPolygon
  load: (cb: () => void) => void
  event: {
    addListener: (target: KakaoMap | KakaoPolygon, type: string, handler: () => void) => void
  }
}

export interface KakaoPolygon {
  setMap: (m: KakaoMap | null) => void
  setOptions: (opts: { fillOpacity?: number; fillColor?: string }) => void
}

export interface KakaoLatLng {
  readonly __brand?: 'kakao.maps.LatLng'
}

export interface KakaoMap {
  setCenter: (c: KakaoLatLng) => void
  setBounds: (b: { extend: (p: KakaoLatLng) => void }, ...padding: number[]) => void
  setLevel: (
    level: number,
    options?: { anchor?: KakaoLatLng; animate?: boolean | { duration: number } },
  ) => void
  getLevel: () => number
  relayout: () => void
}

declare global {
  interface Window {
    kakao?: { maps?: KakaoMaps }
  }
}

export function loadKakaoMapSdk(): Promise<KakaoMaps> {
  if (sdkPromise !== null) return sdkPromise

  sdkPromise = new Promise((resolve, reject) => {
    const jsKey = readKakaoJsKey()
    if (jsKey === null) {
      reject(new Error('카카오 JS 키가 없다'))
      return
    }

    const el = document.createElement('script')
    // `autoload=false` — 받아지는 즉시가 아니라 우리가 `load()` 를 부를 때 뜬다.
    el.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${jsKey}&autoload=false`
    el.async = true
    el.onload = () => {
      const maps = window.kakao?.maps
      if (maps === undefined) {
        reject(new Error('kakao.maps 가 없다'))
        return
      }
      maps.load(() => resolve(maps))
    }
    el.onerror = () => reject(new Error('지도 스크립트를 받지 못했다'))
    document.head.appendChild(el)
  })
  return sdkPromise
}
